/**
 * AI Providers Page
 *
 * List of supported AI providers for translation.
 */

import { Link } from 'react-router-dom';
import { ExternalLink, BookOpen } from 'lucide-react';
import { useI18n, useLocalizedPath } from '@/lib/i18n';
import './docs.scss';

interface Provider {
  key: string;
  docsUrl: string;
  tutorialPath?: string;
}

const providers: Provider[] = [
  { key: 'openai', docsUrl: 'https://platform.openai.com', tutorialPath: '/docs/tutorials/openai-setup' },
  { key: 'gemini', docsUrl: 'https://aistudio.google.com', tutorialPath: '/docs/tutorials/gemini-setup' },
  // Sokuji accepts both Palabra credential kinds; link the newer Platform console (Sokuji's default mode).
  { key: 'palabra', docsUrl: 'https://platform.palabra.ai/api-keys', tutorialPath: '/docs/tutorials/palabraai-setup' },
  { key: 'volcengineAST2', docsUrl: 'https://www.volcengine.com/docs/6561/1631605?lang=zh', tutorialPath: '/docs/tutorials/volcengine-ast2-setup' },
  { key: 'soniox', docsUrl: 'https://soniox.com', tutorialPath: '/docs/tutorials/soniox-setup' },
  // Two storefronts sell the same model; the international one is linked, the guide names both.
  { key: 'qwen', docsUrl: 'https://www.qwencloud.com', tutorialPath: '/docs/tutorials/qwen-setup' },
  { key: 'localInference', docsUrl: 'https://github.com/kizuna-ai-lab/sokuji', tutorialPath: '/docs/tutorials/local-inference-setup' },
];

export function AIProviders() {
  const { t } = useI18n();
  const lp = useLocalizedPath();

  return (
    <div className="docs-content providers-page">
      <h1>{t('providers.title')}</h1>
      <p>{t('providers.subtitle')}</p>

      <div className="providers-page__info-box">
        <h3>{t('providers.setup.title')}</h3>
        <p>{t('providers.setup.desc')}</p>
      </div>

      <div className="providers-page__grid">
        {providers.map((provider) => {
          const features = t(`providers.${provider.key}.features`).split('|');

          return (
            <div key={provider.key} className="providers-page__card">
              <h3>{t(`providers.${provider.key}.name`)}</h3>
              <div className="providers-page__type">
                {t(`providers.${provider.key}.type`)}
              </div>

              <ul className="providers-page__features">
                {features.map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>

              <div className="providers-page__actions">
                <a
                  href={provider.docsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="providers-page__btn providers-page__btn--primary"
                >
                  {t('providers.docs')}
                  <ExternalLink size={14} />
                </a>
                {provider.tutorialPath && (
                  <Link to={lp(provider.tutorialPath)} className="providers-page__btn providers-page__btn--secondary">
                    {t('providers.setupTutorial')}
                    <BookOpen size={14} />
                  </Link>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="providers-page__info-box">
        <h3>{t('providers.choosing.title')}</h3>
        <p><strong>OpenAI:</strong> {t('providers.openai.desc')}</p>
        <p><strong>Gemini:</strong> {t('providers.gemini.desc')}</p>
        <p><strong>PalabraAI:</strong> {t('providers.palabra.desc')}</p>
        <p><strong>Doubao AST 2.0:</strong> {t('providers.volcengineAST2.desc')}</p>
        <p><strong>Soniox:</strong> {t('providers.soniox.desc')}</p>
        <p><strong>Qwen LiveTranslate:</strong> {t('providers.qwen.desc')}</p>
        <p><strong>Local Inference:</strong> {t('providers.localInference.desc')}</p>
      </div>

      <div className="providers-page__info-box">
        <h3>{t('providers.needHelp.title')}</h3>
        <p>
          {t('providers.needHelp.desc')}{' '}
          <a
            href="https://github.com/kizuna-ai-lab/sokuji"
            target="_blank"
            rel="noopener noreferrer"
          >
            GitHub Repository
          </a>
        </p>
      </div>
    </div>
  );
}
